import { environment } from "../../config/environment";
import { customFetch } from "../../utils/Auth";

/**
 * Ask the server to re-read the CS playbook from its ClickUp Doc.
 *
 * Fire-and-forget by design: the server answers 202 without waiting on
 * ClickUp, and a failure here must never block the caller (it only means the
 * agent keeps using the cached playbook until the next scheduled refresh).
 */
export async function refreshPlaybook() {
  try {
    await customFetch(`${environment.apiUrl}/support/playbook/refresh`, {
      method: "POST",
    });
  } catch {
    // Non-fatal: the background refresh timer still covers this.
  }
}

/**
 * Fetch a Pylon ticket bundle: ticket, conversation messages, requester
 * contact, and the matching doctor (by requester email), if any.
 */
export async function getSupportTicket(ticketId) {
  try {
    const response = await customFetch(
      `${environment.apiUrl}/support/tickets/${encodeURIComponent(ticketId)}`,
      { method: "GET" }
    );
    const data = await response.json();
    if (!response.ok) {
      return { error: data?.error || "Failed to fetch ticket" };
    }
    return data;
  } catch {
    return { error: "Failed to fetch ticket" };
  }
}

/**
 * Fetch the ticket board buckets (New, Engineering) shown on the Support
 * homepage. Returns { buckets: [{ key, label, state, tickets, error }] }.
 */
export async function getTicketBuckets() {
  try {
    const response = await customFetch(
      `${environment.apiUrl}/support/tickets/buckets`,
      { method: "GET" }
    );
    const data = await response.json();
    if (!response.ok) {
      return { error: data?.error || "Failed to fetch ticket buckets" };
    }
    return data;
  } catch {
    return { error: "Failed to fetch ticket buckets" };
  }
}

/**
 * Send an engineering escalation to the Slack channel. Includes the agent's
 * reasoning and structured summary fields (reported / identifiers /
 * pipeline_findings / already_tried / investigate), pipeline errors for the
 * patient (fetched server-side), and an optional custom CS message.
 *
 * The server opens a Pylon internal thread that Pylon mirrors into the
 * engineering Slack channel, posts the full context there, and opens a linked
 * ClickUp task. Resolves to { channel, delivery, headline, slack_permalink,
 * clickup_url, clickup_status, clickup_error, pylon_warning }.
 *
 * delivery is "pylon_thread" when the escalation is bridged to the ticket, or
 * "slack" when it fell back to posting in Slack alone — in which case
 * pylon_warning explains why and replies will not sync back to Pylon. A
 * non-null clickup_error means the escalation went out but the task did not.
 */
export async function escalateToSlack({
  ticketId,
  patientId,
  reason,
  customMessage,
  shareUrl,
  payload = {},
  clickupTaskId,
} = {}) {
  try {
    const response = await customFetch(
      `${environment.apiUrl}/support/tickets/escalate`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ticket_id: ticketId || undefined,
          patient_id: patientId || undefined,
          reason: reason || undefined,
          custom_message: customMessage || undefined,
          share_url: shareUrl || undefined,
          clickup_task_id: clickupTaskId || undefined,
          reported: payload.reported || undefined,
          identifiers: payload.identifiers || undefined,
          pipeline_findings: payload.pipeline_findings || undefined,
          already_tried: payload.already_tried || undefined,
          investigate: payload.investigate?.length
            ? payload.investigate
            : undefined,
          engineering_summary: payload.engineering_summary || undefined,
        }),
      }
    );
    const data = await response.json();
    if (!response.ok) {
      return {
        error: data?.error || "Failed to send escalation to Slack",
        clickup_task_id: data?.clickup_task_id || null,
      };
    }
    return data;
  } catch {
    return { error: "Failed to send escalation to Slack" };
  }
}

/**
 * POST to an agent endpoint that streams newline-delimited JSON events.
 * Each parsed event is passed to onEvent (types: status, tool_start, step,
 * patient_resolved, result, reply, error).
 *
 * Returns the final "result" or "reply" event, { error }, or
 * { aborted: true } if the signal fired.
 */
async function streamAgentRequest(url, body, { onEvent, signal } = {}) {
  try {
    const response = await customFetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    });

    // Early failures (missing API key, expired session, etc.) come back as
    // plain JSON before the stream starts.
    const contentType = response.headers.get("content-type") || "";
    if (!contentType.includes("ndjson")) {
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        return { error: data?.error || "Agent request failed" };
      }
      return data || { error: "Empty agent response" };
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let final = null;
    let errorEvent = null;

    const handleLine = (line) => {
      if (!line.trim()) return;
      let event;
      try {
        event = JSON.parse(line);
      } catch {
        return;
      }
      onEvent?.(event);
      if (event.type === "result" || event.type === "reply") final = event;
      if (event.type === "error") errorEvent = event;
    };

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop();
      lines.forEach(handleLine);
    }
    handleLine(buffer);

    if (final) return final;
    return { error: errorEvent?.error || "Agent ended without a response" };
  } catch (err) {
    if (err?.name === "AbortError") {
      return { aborted: true };
    }
    return { error: "Agent request failed" };
  }
}

/**
 * Quick summary: returns { summary, patient_name, appointment_date,
 * issue_category, model }. No streaming, no agent loop.
 */
export async function summarizeTicket(ticketId) {
  try {
    const response = await customFetch(
      `${environment.apiUrl}/support/tickets/${encodeURIComponent(ticketId)}/summarize`,
      { method: "POST", headers: { "Content-Type": "application/json" } }
    );
    const data = await response.json();
    if (!response.ok) {
      return { error: data?.error || "Failed to summarize ticket" };
    }
    return data;
  } catch {
    return { error: "Failed to summarize ticket" };
  }
}

/**
 * Start a new agent analysis session for a ticket.
 * csContext: { patient_ids, doctor_ids, additional_notes, validator_results }
 * — selections and extra context provided by the CS agent before kicking off
 * the analysis. The final "result" event includes session_id for follow-up chat.
 */
export async function analyzeSupportTicket(
  ticketId,
  { csContext, onEvent, signal } = {}
) {
  return streamAgentRequest(
    `${environment.apiUrl}/support/tickets/${encodeURIComponent(ticketId)}/analyze`,
    {
      patient_ids: csContext?.patient_ids?.length ? csContext.patient_ids : undefined,
      doctor_ids: csContext?.doctor_ids?.length ? csContext.doctor_ids : undefined,
      additional_notes: csContext?.additional_notes || undefined,
      validator_results: csContext?.validator_results?.length
        ? csContext.validator_results
        : undefined,
    },
    { onEvent, signal }
  );
}

/**
 * Standalone agent chat (no ticket context). The first call omits sessionId
 * and the returned "reply"/"result" event carries the session_id to reuse.
 * The agent retrieves tickets, patients, and doctors itself via tools when
 * the message references them.
 *
 * seedContext ({ patient, doctor, validator_results, smartChart }) is sent
 * only with the first message of a session, pre-loading the agent with data
 * the page already has (e.g. the patient lookup page).
 */
export async function chatWithSupportAgent({
  sessionId,
  message,
  seedContext,
  onEvent,
  signal,
} = {}) {
  return streamAgentRequest(
    `${environment.apiUrl}/support/tickets/agent-chat`,
    {
      session_id: sessionId || undefined,
      message,
      seed_context: !sessionId && seedContext ? seedContext : undefined,
    },
    { onEvent, signal }
  );
}

/**
 * Snapshot a live agent session (server messages + the given client UI
 * state) to DynamoDB so it can be shared by link. Returns { share_id } or
 * { error }.
 */
export async function shareAgentSession({ sessionId, ui } = {}) {
  try {
    const response = await customFetch(
      `${environment.apiUrl}/support/tickets/share`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, ui }),
      }
    );
    const data = await response.json();
    if (!response.ok) {
      return { error: data?.error || "Failed to share session" };
    }
    return data;
  } catch {
    return { error: "Failed to share session" };
  }
}

/**
 * Load a shared session snapshot. The server forks it into a fresh session,
 * so the returned session_id is new and continuing the chat never mutates
 * the shared snapshot.
 */
export async function getSharedSession(shareId) {
  try {
    const response = await customFetch(
      `${environment.apiUrl}/support/tickets/share/${encodeURIComponent(shareId)}`,
      { method: "GET" }
    );
    const data = await response.json();
    if (!response.ok) {
      return { error: data?.error || "Failed to load shared session" };
    }
    return data;
  } catch {
    return { error: "Failed to load shared session" };
  }
}

/**
 * Send a follow-up message to an existing agent session. The agent may run
 * more lookups, then returns either an updated recommendation ("result") or
 * a conversational reply ("reply").
 */
export async function chatWithTicketAgent(
  ticketId,
  { sessionId, message, onEvent, signal } = {}
) {
  return streamAgentRequest(
    `${environment.apiUrl}/support/tickets/${encodeURIComponent(ticketId)}/chat`,
    { session_id: sessionId, message },
    { onEvent, signal }
  );
}
